import React, { useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import IPInput from '../../../../../components/IPInput/IPInput'
import IPButton from '../../../../../components/IPButton/IPButton'
import Table from '../../../../../components/Table'
import {
  BaseComponentWithContextProps,
  componentWithContext,
} from '../../../../../base/customHooks/componentHOC'

import {
  IDealerPaymentBlockList,
  IDealerPaymentBlockUpdateRequest,
} from '../models/models'

import { DealerBlockListApi } from '../services/dealerBlockListApi'
import { AddDealerBlockApi } from '../services/addDealerBlockApi'
import { getCompanyApi } from '../../../components/api'
import { ICompany } from '../../../components/models/models'

type EditablePaymentBlockRow = IDealerPaymentBlockList & {
  _isDirty?: boolean
}

const TYPE_OPTIONS = ['Order', 'İyzico', 'Compay', 'Payment']

function DealerPaymentBlockList(props: BaseComponentWithContextProps) {
  const [loading, setLoading] = useState(false)
  const [savingAll, setSavingAll] = useState(false)

  const [rows, setRows] = useState<EditablePaymentBlockRow[]>([])
  const [newRow, setNewRow] = useState<Partial<IDealerPaymentBlockList> | null>(null)
  const [companies, setCompanies] = useState<ICompany[]>([])

  const normalizeTypeByCompany = (
    typeByCompany?: Record<string, string[]>
  ): Record<string, string[]> => {
    return typeByCompany ?? {}
  }

  const getCompanyList = (typeByCompany?: Record<string, string[]>) => {
    return Object.keys(normalizeTypeByCompany(typeByCompany))
  }

  const getCompanyCode = (company: ICompany) => {
    return (
      (company as any).Code ||
      (company as any).CompanyCode ||
      (company as any).SapCode ||
      (company as any).Sapcode ||
      (company as any).Id ||
      ''
    ).toString()
  }

  const getCompanyName = (company: ICompany) => {
    return (
      (company as any).Name ||
      (company as any).CompanyName ||
      getCompanyCode(company)
    ).toString()
  }

  const showError = (title: string, err: any) => {
    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      err?.description ||
      err ||
      'Beklenmeyen bir hata oluştu.'

    Swal.fire({
      icon: 'error',
      title,
      text: String(msg),
    })
  }

  const getCompanies = async () => {
    try {
      ; (props as any).showLoading?.()
      const company: any[] = await getCompanyApi()
      const data: ICompany[] = (company || []).filter(
        com => com.Name !== 'Index Group'
      )

      setCompanies(data)
    } catch (err) {
      showError('Şirket listesi getirilemedi', err)
    } finally {
      ; (props as any).hideLoading?.()
    }
  }

  const fetchList = async () => {
    ; (props as any).showLoading?.()
    setLoading(true)

    try {
      const api = new DealerBlockListApi({})
      const data = await api.GetDealerBlock()

      const list: IDealerPaymentBlockList[] = Array.isArray(data)
        ? data
        : (data as any)?.data ?? []

      setRows(
        (list || []).map(x => ({
          ...x,
          TypeByCompany: normalizeTypeByCompany(x.TypeByCompany),
          Company: x.Company?.length ? x.Company : getCompanyList(x.TypeByCompany),
          _isDirty: false,
        }))
      )
    } catch (err) {
      showError('Liste getirilemedi', err)
    } finally {
      setLoading(false)
        ; (props as any).hideLoading?.()
    }
  }

  useEffect(() => {
    getCompanies()
    fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const markRowDirty = (
    index: number,
    updater: (row: EditablePaymentBlockRow) => EditablePaymentBlockRow
  ) => {
    setRows(prev => {
      const copy = [...prev]

      copy[index] = {
        ...updater({ ...copy[index] }),
        _isDirty: true,
      }

      return copy
    })
  }

  const onToggleDeleted = (index: number, checked: boolean) => {
    markRowDirty(index, row => ({
      ...row,
      Deleted: checked,
    }))
  }

  const onChangeCompanyCode = (
    index: number,
    oldCompanyCode: string,
    newCompanyCode: string
  ) => {
    const cleanCompanyCode = newCompanyCode.trim()

    markRowDirty(index, row => {
      const typeByCompany = { ...normalizeTypeByCompany(row.TypeByCompany) }
      const currentTypes = typeByCompany[oldCompanyCode] ?? []

      if (!cleanCompanyCode) return row

      if (oldCompanyCode !== cleanCompanyCode && typeByCompany[cleanCompanyCode]) {
        Swal.fire({
          icon: 'warning',
          title: 'Şirket mevcut',
          text: 'Bu şirket zaten satıra eklenmiş.',
        })

        return row
      }

      if (oldCompanyCode) {
        delete typeByCompany[oldCompanyCode]
      }

      typeByCompany[cleanCompanyCode] = currentTypes

      return {
        ...row,
        TypeByCompany: typeByCompany,
        Company: getCompanyList(typeByCompany),
      }
    })
  }

  const onRemoveCompanyFromRow = (index: number, companyCode: string) => {
    markRowDirty(index, row => {
      const typeByCompany = { ...normalizeTypeByCompany(row.TypeByCompany) }

      delete typeByCompany[companyCode]

      return {
        ...row,
        TypeByCompany: typeByCompany,
        Company: getCompanyList(typeByCompany),
      }
    })
  }

  const onToggleType = (
    index: number,
    companyCode: string,
    type: string,
    checked: boolean
  ) => {
    markRowDirty(index, row => {
      const typeByCompany = { ...normalizeTypeByCompany(row.TypeByCompany) }
      const currentTypes = typeByCompany[companyCode] ?? []

      typeByCompany[companyCode] = checked
        ? Array.from(new Set([...currentTypes, type]))
        : currentTypes.filter(x => x !== type)

      return {
        ...row,
        TypeByCompany: typeByCompany,
        Company: getCompanyList(typeByCompany),
      }
    })
  }

  const onAddNewRow = () => {
    if (newRow) return

    setNewRow({
      Id: 0,
      DealerCode: '',
      TypeByCompany: {},
      Company: [],
      Deleted: false,
    })
  }

  const onCancelNewRow = () => {
    setNewRow(null)
  }

  const onChangeNewDealerCode = (value: string) => {
    setNewRow(prev => ({
      ...(prev || {}),
      DealerCode: value,
    }))
  }

  const onToggleNewCompany = (companyCode: string, checked: boolean) => {
    setNewRow(prev => {
      const typeByCompany = { ...(prev?.TypeByCompany ?? {}) }

      if (checked) {
        if (!typeByCompany[companyCode]) {
          typeByCompany[companyCode] = []
        }
      } else {
        delete typeByCompany[companyCode]
      }

      return {
        ...(prev || {}),
        TypeByCompany: typeByCompany,
        Company: Object.keys(typeByCompany),
      }
    })
  }

  const onToggleNewType = (
    companyCode: string,
    type: string,
    checked: boolean
  ) => {
    setNewRow(prev => {
      const typeByCompany = { ...(prev?.TypeByCompany ?? {}) }
      const currentTypes = typeByCompany[companyCode] ?? []

      typeByCompany[companyCode] = checked
        ? Array.from(new Set([...currentTypes, type]))
        : currentTypes.filter(x => x !== type)

      return {
        ...(prev || {}),
        TypeByCompany: typeByCompany,
        Company: Object.keys(typeByCompany),
      }
    })
  }

  const validatePayload = (payload: IDealerPaymentBlockUpdateRequest[]) => {
    const emptyDealerCode = payload.some(x => !x.DealerCode?.trim())

    if (emptyDealerCode) {
      Swal.fire({
        icon: 'warning',
        title: 'Eksik bilgi',
        text: 'Bayi kodu boş olan kayıt var.',
      })

      return false
    }

    const emptyTypeByCompany = payload.some(
      x => Object.keys(x.TypeByCompany ?? {}).length === 0
    )

    if (emptyTypeByCompany) {
      Swal.fire({
        icon: 'warning',
        title: 'Eksik bilgi',
        text: 'En az bir şirket seçilmelidir.',
      })

      return false
    }

    const emptyCompanyCode = payload.some(x =>
      Object.keys(x.TypeByCompany ?? {}).some(companyCode => !companyCode?.trim())
    )

    if (emptyCompanyCode) {
      Swal.fire({
        icon: 'warning',
        title: 'Eksik bilgi',
        text: 'Şirket kodu boş bırakılamaz.',
      })

      return false
    }

    const emptyTypes = payload.some(x =>
      Object.values(x.TypeByCompany ?? {}).some(
        types => !types || types.length === 0
      )
    )

    if (emptyTypes) {
      Swal.fire({
        icon: 'warning',
        title: 'Eksik bilgi',
        text: 'Her şirket için en az bir type seçilmelidir.',
      })

      return false
    }

    return true
  }

  const onSaveNewRow = async () => {
    if (!newRow) return

    const payload: IDealerPaymentBlockUpdateRequest[] = [
      {
        Id: 0,
        DealerCode: (newRow.DealerCode || '').toString().trim(),
        TypeByCompany: normalizeTypeByCompany(newRow.TypeByCompany),
        Deleted: false,
      },
    ]

    if (!validatePayload(payload)) return

    setSavingAll(true)
      ; (props as any).showLoading?.()

    try {
      const api = new AddDealerBlockApi({})
      const res = await api.AddDealerBlock(payload as any)

      const message = (res as any)?.data ?? res ?? 'Kayıt eklendi.'

      Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: String(message),
      })

      setNewRow(null)
      await fetchList()
    } catch (err) {
      showError('Ekleme başarısız', err)
    } finally {
      setSavingAll(false)
        ; (props as any).hideLoading?.()
    }
  }

  const onSaveAll = async () => {
    const dirtyRows = rows.filter(r => r._isDirty)

    if (dirtyRows.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Kayıt yok',
        text: 'Güncellenecek kayıt bulunamadı.',
      })

      return
    }

    const payload: IDealerPaymentBlockUpdateRequest[] = dirtyRows.map(r => ({
      Id: r.Id,
      DealerCode: (r.DealerCode || '').trim(),
      TypeByCompany: normalizeTypeByCompany(r.TypeByCompany),
      Deleted: !!r.Deleted,
    }))

    if (!validatePayload(payload)) return

    setSavingAll(true)
      ; (props as any).showLoading?.()

    try {
      const api = new AddDealerBlockApi({})
      const res = await api.AddDealerBlock(payload as any)

      const message = (res as any)?.data ?? res ?? 'Güncelleme tamamlandı.'

      Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: String(message),
      })

      await fetchList()
    } catch (err) {
      showError('Güncelleme başarısız', err)
    } finally {
      setSavingAll(false)
        ; (props as any).hideLoading?.()
    }
  }

  const renderTypeCheckboxes = (
    companyCode: string,
    selectedTypes: string[],
    onToggle: (companyCode: string, type: string, checked: boolean) => void
  ) => {
    return (
      <div className="d-flex flex-wrap gap-3">
        {TYPE_OPTIONS.map(type => (
          <label key={type} className="d-flex align-items-center gap-1 mb-0">
            <input
              type="checkbox"
              checked={selectedTypes.includes(type)}
              onChange={e => onToggle(companyCode, type, e.target.checked)}
            />
            <span>{type}</span>
          </label>
        ))}
      </div>
    )
  }

  const renderNewRowCompanySelector = () => {
    const typeByCompany = normalizeTypeByCompany(newRow?.TypeByCompany)
    const selectedCompanies = Object.keys(typeByCompany)

    return (
      <div>
        <div className="mb-3">
          <label className="form-label fw-bold">
            Şirketler
          </label>

          <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
            {companies.length === 0 && (
              <div className="text-muted">Şirket bulunamadı.</div>
            )}

            {companies.map(company => {
              const code = getCompanyCode(company)
              const name = getCompanyName(company)
              const checked = selectedCompanies.includes(code)

              return (
                <label
                  key={code}
                  className="d-flex align-items-center gap-2 mb-2"
                  style={{ cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={e => onToggleNewCompany(code, e.target.checked)}
                  />

                  <span>
                    {name}
                  </span>
                </label>
              )
            })}
          </div>
        </div>

        {selectedCompanies.length > 0 && (
          <div className="mt-3">
            <label className="form-label fw-bold">
              Seçilen şirketler için işlem türü seçimi
            </label>

            {selectedCompanies.map(companyCode => {
              const company = companies.find(c => getCompanyCode(c) === companyCode)
              const companyName = company ? getCompanyName(company) : companyCode
              const selectedTypes = typeByCompany[companyCode] ?? []

              return (
                <div key={companyCode} className="border rounded p-2 mb-2">
                  <div className="row g-2 align-items-center">
                    <div className="col-md-3">
                      <div className="fw-bold">{companyName}</div>
                    </div>

                    <div className="col-md-7">
                      {renderTypeCheckboxes(
                        companyCode,
                        selectedTypes,
                        onToggleNewType
                      )}
                    </div>

                    <div className="col-md-2 text-end">
                      <IPButton
                        text="Kaldır"
                        onClick={() => onToggleNewCompany(companyCode, false)}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const renderTypeByCompanyEditor = (
    typeByCompany: Record<string, string[]>,
    handlers: {
      onChangeCompanyCode: (
        oldCompanyCode: string,
        newCompanyCode: string
      ) => void
      onRemoveCompany: (companyCode: string) => void
      onToggleType: (
        companyCode: string,
        type: string,
        checked: boolean
      ) => void
    }
  ) => {
    const selectedCompanies = Object.keys(typeByCompany ?? {})
    const availableCompanies = companies.filter(
      c => !selectedCompanies.includes(getCompanyCode(c))
    )

    return (
      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {selectedCompanies.length === 0 && (
          <div className="text-muted small">Şirket eklenmemiş.</div>
        )}

        {selectedCompanies.map(companyCode => {
          const selectedTypes = typeByCompany[companyCode] ?? []
          const company = companies.find(c => getCompanyCode(c) === companyCode)
          const companyName = company ? getCompanyName(company) : companyCode

          return (
            <div key={companyCode} className="border-bottom pb-2 mb-2">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                  <div
                    className="fw-500"
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {companyName}
                  </div>
                </div>

                <div
                  className="d-flex justify-content-end"
                  style={{
                    flexWrap: 'nowrap',
                    fontSize: '0.9rem',
                    minWidth: 'fit-content',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    marginRight: '1.25rem',
                  }}
                >
                  {TYPE_OPTIONS.map(type => (
                    <label
                      key={type}
                      className="d-flex align-items-center gap-1 mb-0"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTypes.includes(type)}
                        onChange={e =>
                          handlers.onToggleType(
                            companyCode,
                            type,
                            e.target.checked
                          )
                        }
                        style={{ cursor: 'pointer' }}
                      />

                      <span>{type}</span>
                    </label>
                  ))}
                </div>

                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handlers.onRemoveCompany(companyCode)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  Sil
                </button>
              </div>
            </div>
          )
        })}

        {availableCompanies.length > 0 && (
          <select
            className="form-select form-select-sm"
            onChange={e => {
              if (e.target.value) {
                handlers.onChangeCompanyCode('', e.target.value)
                e.target.value = ''
              }
            }}
            defaultValue=""
          >
            <option value="">Şirket Ekle...</option>

            {availableCompanies.map(company => {
              const code = getCompanyCode(company)
              const name = getCompanyName(company)

              return (
                <option key={code} value={code}>
                  {name}
                </option>
              )
            })}
          </select>
        )}

        {availableCompanies.length === 0 && selectedCompanies.length > 0 && (
          <div className="text-muted small mt-2">
            Tüm şirketler eklendi.
          </div>
        )}
      </div>
    )
  }

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Sil',
        accessor: 'delete',
        Cell: ({ row }: any) => {
          const i = rows.indexOf(row.original)

          return (
            <div className="text-center">
              <input
                type="checkbox"
                checked={!!row.original.Deleted}
                onChange={e => onToggleDeleted(i, e.target.checked)}
              />
            </div>
          )
        },
      },
      {
        Header: 'Bayi Kodu',
        accessor: 'DealerCode',
        width: 70,
        Cell: ({ row }: any) => (
          <IPInput
            value={(row.original.DealerCode?.replace('0000', '') ?? '') as any}
            type="text"
            disabled
          />
        ),
      },
      {
        Header: 'Bayi Adı',
        accessor: 'DealerName',
        Cell: ({ row }: any) => (
          <IPInput
            value={(row.original.DealerName ?? '') as any}
            disabled
            type="text"
          />
        ),
      },
      {
        Header: 'Şirket / İşlem Türü',
        accessor: 'TypeByCompany',
        Cell: ({ row }: any) => {
          const i = rows.indexOf(row.original)

          return renderTypeByCompanyEditor(
            normalizeTypeByCompany(row.original.TypeByCompany),
            {
              onChangeCompanyCode: (oldCompanyCode, newCompanyCode) =>
                onChangeCompanyCode(i, oldCompanyCode, newCompanyCode),
              onRemoveCompany: companyCode =>
                onRemoveCompanyFromRow(i, companyCode),
              onToggleType: (companyCode, type, checked) =>
                onToggleType(i, companyCode, type, checked),
            }
          )
        },
      },
      {
        Header: 'Durum',
        accessor: 'status',
        Cell: ({ row }: any) => (
          <>
            {row.original.Deleted ? (
              <span className="badge bg-danger">Silinecek</span>
            ) : row.original._isDirty ? (
              <span className="badge bg-warning text-dark">Değiştirildi</span>
            ) : (
              <span className="badge bg-secondary">Değişmedi</span>
            )}
          </>
        ),
      },
    ],
    [rows, companies]
  )

  const dirtyCount = useMemo(
    () => rows.filter(r => r._isDirty).length,
    [rows]
  )

  return (
    <div className="">
      <div className="content col-lg-12 m-auto">
        <div className="card">
          <div className="card-header d-flex align-items-center">
            <span className="h4 mb-0">Bayi Ödeme Bloke Listesi</span>

            <div className="d-flex gap-2 ms-auto me-3">
              <IPButton
                text={loading ? 'Yükleniyor...' : 'Yenile'}
                disabled={loading || savingAll}
                onClick={fetchList}
              />

              <IPButton
                text="Ekle"
                disabled={loading || savingAll || !!newRow}
                onClick={onAddNewRow}
              />

              <IPButton
                text={
                  savingAll
                    ? 'Kaydediliyor...'
                    : `Değişiklikleri Kaydet ${dirtyCount > 0 ? ` - ${dirtyCount}` : ''
                    }`
                }
                disabled={loading || savingAll || dirtyCount === 0}
                onClick={onSaveAll}
              />
            </div>
          </div>

          {rows?.length > 0 && (
            <div className="card-body">
              <b>Toplam Veri Sayısı: {rows.length}</b>
            </div>
          )}

          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">Liste yükleniyor...</div>
            ) : (
              <>
                {newRow && (
                  <div className="mb-3 p-3 border rounded">
                    <div className="row g-2 align-items-end mb-3">
                      <div className="col-md-2">
                        <label className="form-label">Bayi Kodu</label>

                        <IPInput
                          value={(newRow.DealerCode ?? '') as any}
                          onChange={(e: any) =>
                            onChangeNewDealerCode(e?.target?.value ?? e)
                          }
                          type="text"
                        />
                      </div>

                      <div className="col-md-3 d-flex gap-2">
                        <IPButton
                          text="Kaydet"
                          disabled={savingAll}
                          onClick={onSaveNewRow}
                        />

                        <IPButton
                          text="İptal"
                          disabled={savingAll}
                          onClick={onCancelNewRow}
                        />
                      </div>
                    </div>

                    {renderNewRowCompanySelector()}
                  </div>
                )}

                {rows.length === 0 ? (
                  <div className="text-center py-4">Kayıt bulunamadı.</div>
                ) : (
                  <Table columns={tableColumns} data={rows} />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default componentWithContext(DealerPaymentBlockList)